import { useState, useEffect, useRef, Fragment } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import api from '../services/api'
import AvisoUltimaNota from '../components/AvisoUltimaNota'
import NaturezaSelect from '../components/NaturezaSelect'
import ItemSelect from '../components/ItemSelect'
import IbsCbsSelect from '../components/IbsCbsSelect'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UF_IBGE: Record<string, string> = {
  AC:'12',AL:'27',AP:'16',AM:'13',BA:'29',CE:'23',DF:'53',ES:'32',
  GO:'52',MA:'21',MT:'51',MS:'50',MG:'31',PA:'15',PB:'25',PR:'41',
  PE:'26',PI:'22',RJ:'33',RN:'24',RS:'43',RO:'11',RR:'14',SC:'42',
  SP:'35',SE:'28',TO:'17',
}
function calcDV(c43: string): number {
  const p = [2,3,4,5,6,7,8,9]; let s = 0
  for (let i = c43.length-1, j=0; i>=0; i--,j++) s += parseInt(c43[i]) * p[j%8]
  const r = s % 11; return r < 2 ? 0 : 11 - r
}
function gerarChave(empcgc: string, empest: string, dataEmissao: string, serie: string, numero: string, cNF: string) {
  const cuf = UF_IBGE[empest.trim()] ?? '43'
  const aamm = dataEmissao.replace(/-/g,'').slice(2,6)
  const cnpj = empcgc.replace(/\D/g,'').padStart(14,'0')
  const ser = serie.padStart(3,'0'); const num = numero.padStart(9,'0')
  const c43 = `${cuf}${aamm}${cnpj}55${ser}${num}1${cNF}`
  return c43 + calcDV(c43)
}
function randCNF() { return String(Math.floor(Math.random()*99999999)).padStart(8,'0') }
function formatDoc(doc: string) {
  const n = doc.replace(/\D/g,'')
  if (n.length===14) return n.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,'$1.$2.$3/$4-$5')
  if (n.length===11) return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/,'$1.$2.$3-$4')
  return doc
}
const r2 = (n: number) => Math.round(n*100)/100
const brl = (n: number) => Number(n||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'})

// ─── Tipos de Nota de Crédito (NT 2025.002-RTC) ────────────────────────────────
//
// modo 'origem': destinatário/CST/cClassTrib herdados de uma NF origem obrigatória,
//                referenciada a nível de nota (NFref) — tipos 01, 03 e 04.
// modo 'manual': destinatário pesquisado, item informado pelo usuário, sem NF
//                origem — tipo 05.
// Tipo 01 (Multa e Juros): caso especial de 'origem' (rateioValor=true) — itens
// herdam CST/cClassTrib da nota referenciada, mas o crédito é por valor: o
// usuário informa o total de multa/juros pago e o sistema rateia entre os itens
// selecionados, proporcional ao valor de cada um (método do maior resto, fecha
// exato no centavo). Tributos clássicos ficam zerados (regra B25-80).
// Tipo 06 (referenciamento a nível de item — grupo DFeReferenciado ainda não
// existe no XML builder) fica pra depois. Tipo 02 (ZFM) fica bloqueado pela
// própria SEFAZ até 2029 — não vale a pena construir a tela antes disso.

interface TipoCreditoConfig {
  codigo: string
  label: string
  habilitado: boolean
  motivo?: string
  modo: 'origem' | 'manual'
  // 'devolucao' = mesma restrição de CFOP da devolução de mercadoria (I08-140)
  // 'normal'    = CFOP de devolução é vedado (I08-144)
  cfopRestricao: 'devolucao' | 'normal'
  // Regra B25-80 da NT: fora dos tipos abaixo, a nota de crédito só carrega IBS/CBS
  tributosClassicos: boolean
  // Rótulo do card de destinatário no modo 'manual' — cada tipo tem um papel diferente
  destinatarioLabel?: string
  // Dica de CST/cClassTrib esperado mostrada acima do seletor IBS/CBS (modo 'manual').
  // Regra UB14-80 da NT: alguns tipos exigem cClassTrib fixo, outros ("Não limitar") não têm
  // valor obrigatório — o usuário escolhe o que reflete a tributação real da operação.
  regraEsperada: string
  // Tipo 01: itens herdados da nota de origem (CST/cClassTrib de cada item, não editável),
  // mas o crédito é lançado por valor (total de multa/juros informado pelo usuário, rateado
  // proporcionalmente entre os itens selecionados) em vez de por quantidade.
  rateioValor?: boolean
  // Tipo 04: o valor unitário do item é editável (redução de valores cobrados a maior) —
  // diferente de 03/06, que mantêm o valor da nota original.
  valorEditavel?: boolean
}

const TIPOS_CREDITO: TipoCreditoConfig[] = [
  { codigo: '01', label: '01 — Multa e Juros', habilitado: true, modo: 'origem', rateioValor: true, cfopRestricao: 'normal', tributosClassicos: false, regraEsperada: '' },
  { codigo: '02', label: '02 — Crédito Presumido ZFM', habilitado: false, motivo: 'bloqueado pela SEFAZ até janeiro/2029', modo: 'manual', cfopRestricao: 'normal', tributosClassicos: false, regraEsperada: 'CST 810 / cClassTrib 810001' },
  { codigo: '03', label: '03 — Retorno Total (recusa/não localizado)', habilitado: true, modo: 'origem', cfopRestricao: 'devolucao', tributosClassicos: true, regraEsperada: '' },
  { codigo: '04', label: '04 — Redução de Valores', habilitado: true, modo: 'origem', valorEditavel: true, cfopRestricao: 'normal', tributosClassicos: true, regraEsperada: '' },
  { codigo: '05', label: '05 — Transferência na Sucessão', habilitado: true, modo: 'manual', cfopRestricao: 'normal', tributosClassicos: false, destinatarioLabel: 'Destinatário (empresa sucessora)', regraEsperada: 'CST 800 / cClassTrib 800001' },
  { codigo: '06', label: '06 — Recusa Parcial na Entrega', habilitado: false, motivo: 'referenciamento a nível de item (DFeReferenciado) — ainda não implementado no XML', modo: 'origem', cfopRestricao: 'devolucao', tributosClassicos: true, regraEsperada: '' },
]

// ─── Interfaces ───────────────────────────────────────────────────────────────

interface Empresa { empcod: number; empnom: string; empcgc: string; empest: string }
interface Natureza {
  tno_cod: number; tno_cdf: number; tno_des: string; tno_dsc: string; tno_nat: string
  tno_tic: string; tno_bic: number; tno_aic: number   // ICMS
  tno_tpi: string; tno_bpi: number; tno_api: number   // PIS
  tno_tcf: string; tno_bcf: number; tno_acf: number   // COFINS
  tno_tin: string; tno_bin: number; tno_ain: number   // IPI
}
interface IbsCbs {
  cst: string; classTrib: string; classTribDes: string
  cbsBc: number; cbsBcEf: number; cbsAliRef: number; cbsRedAli: number; cbsAliEf: number; cbsValor: number
  ibsUfEst: string; ibsUfBcEf: number; ibsUfAliRef: number; ibsUfRedAli: number; ibsUfAliEf: number; ibsUfValor: number
  ibsMunBcEf: number; ibsMunAliRef: number; ibsMunRedAli: number; ibsMunAliEf: number; ibsMunValor: number
  isSbas: number; isSali: number
}
const ibsCbsVazio: IbsCbs = {
  cst:'', classTrib:'', classTribDes:'',
  cbsBc:0, cbsBcEf:0, cbsAliRef:0, cbsRedAli:0, cbsAliEf:0, cbsValor:0,
  ibsUfEst:'', ibsUfBcEf:0, ibsUfAliRef:0, ibsUfRedAli:0, ibsUfAliEf:0, ibsUfValor:0,
  ibsMunBcEf:0, ibsMunAliRef:0, ibsMunRedAli:0, ibsMunAliEf:0, ibsMunValor:0,
  isSbas:0, isSali:0,
}
function calcIbsCbs(total: number, r: any): IbsCbs {
  if (!r || total<=0) return {...ibsCbsVazio}
  const bcEfC = r2(total*(1-Number(r.ibs08_crbc)/100))
  const bcEfU = r2(total*(1-Number(r.ibs08_urbc)/100))
  const bcEfM = r2(total*(1-Number(r.ibs08_mrbc)/100))
  return {
    cst: r.ibs08_cst?.trim()??'', classTrib: r.ibs08_tri?.trim()??'', classTribDes: r.ibs08_d02?.trim()??'',
    cbsBc: total, cbsBcEf: bcEfC, cbsAliRef: Number(r.ibs08_cali), cbsRedAli: Number(r.ibs08_cpra), cbsAliEf: Number(r.ibs08_caef), cbsValor: r2(bcEfC*Number(r.ibs08_caef)/100),
    ibsUfEst: r.ibs08_uest?.trim()??'', ibsUfBcEf: bcEfU, ibsUfAliRef: Number(r.ibs08_uali), ibsUfRedAli: Number(r.ibs08_upra), ibsUfAliEf: Number(r.ibs08_uaef), ibsUfValor: r2(bcEfU*Number(r.ibs08_uaef)/100),
    ibsMunBcEf: bcEfM, ibsMunAliRef: Number(r.ibs08_mali), ibsMunRedAli: Number(r.ibs08_mpra), ibsMunAliEf: Number(r.ibs08_maef), ibsMunValor: r2(bcEfM*Number(r.ibs08_maef)/100),
    isSbas: Number(r.ibs08_sbas), isSali: Number(r.ibs08_sali),
  }
}

interface Cabecalho {
  empcod: number; serie: string; numero: number
  cnpj_fornecedor: string; chave: string; data_emissao: string
  valor_total: number; cfop_original: string; nome_fornecedor: string
  fj: string; ie: string; uf: string; contribuinte: string
  logradouro: string; numero_end: string; bairro: string
  cep: string; telefone: string; cidade: string
  bloqueado?: boolean
}
interface ItemCredito {
  lin: number; its_cod: number; descricao: string; unidade: string
  quantidade: number; preco_unitario: number; preco_unitario_orig: number; ncm: string; cest: string
  icms_aliq: number; icms_base_orig: number; icms_valor_orig: number; icms_cst: string
  pis_aliq: number;  pis_base_orig: number;  pis_valor_orig: number
  cofins_aliq: number; cofins_base_orig: number; cofins_valor_orig: number
  selecionado: boolean; qtd_credito: string; expandido: boolean
  edit_icms_cst: string; edit_icms_base: number; edit_icms_aliq: number; edit_icms_valor: number
  edit_st_base: number; edit_st_aliq: number; edit_st_valor: number
  edit_ipi_cst: string; edit_ipi_base: number; edit_ipi_aliq: number; edit_ipi_valor: number
  edit_pis_cst: string;  edit_pis_base: number;  edit_pis_aliq: number;  edit_pis_valor: number
  edit_cofins_cst: string; edit_cofins_base: number; edit_cofins_aliq: number; edit_cofins_valor: number
  ibsCbs: IbsCbs
}

// Destinatário/item pra tipos em modo 'manual' (ex.: 05 — Transferência na Sucessão)
interface Cliente {
  cli_cod: string; cli_mat: string; cli_nom: string; cli_fan: string
  cli_fj: string; cli_ins: string; cli_est: string; cli_contri: string
  bloqueado?: boolean
}
interface Endereco {
  logradouro: string; numero: string; bairro: string; cep: string
  telefone: string; email: string; cidade: string; uf: string
}
interface ItemManual {
  cProd: string; descricao: string; unidade: string; NCM: string; CEST: string
  quantidade: number; valorUnitario: number
  ibsCbsRegra: string; ibsCbsDes: string
  ibsCbs: IbsCbs
}
const itemManualVazio: ItemManual = {
  cProd:'', descricao:'', unidade:'UN', NCM:'', CEST:'',
  quantidade:1, valorUnitario:0,
  ibsCbsRegra:'', ibsCbsDes:'',
  ibsCbs: { ...ibsCbsVazio },
}

// ─── Componente ───────────────────────────────────────────────────────────────

export default function EmitirNotaCreditoNFe() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editId = searchParams.get('edit')
  const idSalvoRef = useRef<number | null>(null) // nota já gravada nesta tela (reemissão depois de rejeição)

  const [etapa, setEtapa] = useState(0)

  // Tipo de crédito
  const [tipoCredito, setTipoCredito] = useState('03')
  const tipoCfg = TIPOS_CREDITO.find(t => t.codigo === tipoCredito)!

  // Empresa
  const [empresas, setEmpresas] = useState<Empresa[]>([])
  const [empresa, setEmpresa]   = useState<Empresa | null>(null)

  // ── modo 'origem': busca da NF origem ──────────────────────────────────────
  const [chaveInput, setChaveInput] = useState('')
  const [empcod, setEmpcod] = useState('')
  const [cnpj, setCnpj]     = useState('')
  const [serie, setSerie]   = useState('')
  const [numBusca, setNumBusca] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [erroBusca, setErroBusca] = useState('')
  const [cab, setCab]           = useState<Cabecalho | null>(null)
  const [itens, setItens]       = useState<ItemCredito[]>([])

  // ── tipo 01 (rateioValor): valor total de multa/juros informado pelo usuário,
  //    rateado entre os itens herdados da nota de origem ──────────────────────
  const [valorMultaJuros, setValorMultaJuros] = useState('')

  // ── modo 'manual': destinatário pesquisado + itens informados ─────────────
  const [clienteBusca, setClienteBusca] = useState('')
  const [clientesEncontrados, setClientesEncontrados] = useState<Cliente[]>([])
  const [buscandoCliente, setBuscandoCliente] = useState(false)
  const [clienteSel, setClienteSel] = useState<Cliente | null>(null)
  const [clienteEndereco, setClienteEndereco] = useState<Endereco | null>(null)
  const [itensManual, setItensManual] = useState<ItemManual[]>([{ ...itemManualVazio }])
  const debounceCliente = useRef<ReturnType<typeof setTimeout>>()
  // Ao carregar uma nota rejeitada em modo edição, tipoCredito muda de uma vez só —
  // sem isso, o useEffect de observação automática (abaixo) dispararia logo em
  // seguida e sobrescreveria a obs salva pelo texto padrão recalculado.
  const skipAutoObsRef = useRef(false)

  // Próximo número / chave
  const [proxSerie, setProxSerie]   = useState('')
  const [proxNumero, setProxNumero] = useState('')
  const [chaveNFe, setChaveNFe]     = useState('')

  // Configuração
  const [naturezas, setNaturezas]   = useState<Natureza[]>([])
  const [naturezaCod, setNaturezaCod] = useState('')
  const [dataEmissao, setDataEmissao] = useState(new Date().toISOString().slice(0,10))
  const [obs, setObs]               = useState('')

  // Observação sugerida automaticamente (tipo + NF referenciada, quando houver) — pra
  // dar rastreabilidade extra, já que a DANFE por si só não detalha o tipo de crédito/débito.
  // Continua editável: só recalcula quando o tipo ou a NF referenciada mudam.
  useEffect(() => {
    if (tipoCfg.modo !== 'manual') return
    if (skipAutoObsRef.current) { skipAutoObsRef.current = false; return }
    setObs(`Nota de Crédito ${tipoCfg.label}`)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipoCredito])

  // Abas fiscais por item (modo origem)
  const [abaFiscal, setAbaFiscal] = useState<Record<number,'impostos'|'ibscbs'>>({})
  const abaItem = (i: number) => abaFiscal[i] ?? 'impostos'

  // Emissão
  const [enviando, setEnviando]   = useState(false)
  const [resultado, setResultado] = useState<string|null>(null)
  const [erro, setErro]           = useState<string|null>(null)

  useEffect(() => {
    api.get('/erp/empresas').then(r => {
      setEmpresas(r.data)
      if (r.data.length > 0) setEmpresa(r.data[0])
    }).catch(() => {})
    api.get('/erp/naturezas').then(r => setNaturezas(r.data)).catch(() => {})
  }, [])
  useEffect(() => { if (empresa) setEmpcod(String(empresa.empcod)) }, [empresa])

  // ── Modo edição: carrega payload da nota rejeitada/pendente ───────────────
  useEffect(() => {
    if (!editId || !empresas.length || !naturezas.length) return
    api.get(`/nfe/${editId}/payload`).then(r => {
      const fp = r.data?._formPayload
      if (!fp || fp._tipo !== 'NOTA_CREDITO') return
      const emp = empresas.find(e => e.empcod === fp.empcod) ?? empresas[0]
      setEmpresa(emp)
      skipAutoObsRef.current = true
      setTipoCredito(fp.tipoCredito ?? '03')
      setCab(fp.cab ?? null)
      setItens(fp.itens ?? [])
      setClienteSel(fp.clienteSel ?? null)
      setClienteEndereco(fp.clienteEndereco ?? null)
      setItensManual(fp.itensManual?.length ? fp.itensManual : [{ ...itemManualVazio }])
      setValorMultaJuros(fp.valorMultaJuros ?? '')
      setNaturezaCod(String(fp.naturezaCod ?? ''))
      setDataEmissao(fp.dataEmissao ?? new Date().toISOString().slice(0,10))
      setObs(fp.obs ?? '')
      setProxSerie(fp.proxSerie ?? '')
      setProxNumero(fp.proxNumero ?? '')
      setChaveNFe(fp.chaveNFe ?? '')
      setEtapa(1)
    }).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, empresas.length, naturezas.length])

  // Tabela do Leandro: tpNFCredito=03/06 só aceita esses CFOPs de devolução/retorno
  // (RS: 1201-1661; fora do RS: 2201-2661).
  const CFOP_DEVOLUCAO_CREDITO = [
    1201, 1202, 1203, 1208, 1209, 1410, 1411, 1553, 1556, 1660, 1661,
    2201, 2202, 2203, 2208, 2209, 2410, 2411, 2553, 2556, 2660, 2661,
  ]

  const naturezasFiltradas = naturezas
    .filter(n => n.tno_nat === '1')
    .filter(n => tipoCfg.cfopRestricao !== 'devolucao' || CFOP_DEVOLUCAO_CREDITO.includes(n.tno_cdf))

  function trocarTipo(cod: string) {
    setTipoCredito(cod)
    setCab(null); setItens([]); setNaturezaCod('')
    setChaveInput(''); setErroBusca('')
    setClienteSel(null); setClienteEndereco(null); setClienteBusca(''); setClientesEncontrados([])
    setItensManual([{ ...itemManualVazio }])
    setProxSerie(''); setProxNumero(''); setChaveNFe('')
    setValorMultaJuros('')
  }

  async function buscarProximoNumero(empAtual: Empresa) {
    const nr = await api.get(`/erp/proximonumero/${empAtual.empcod}/NFE`)
    setProxSerie(String(nr.data.serie))
    setProxNumero(String(nr.data.numero))
    const cNF = randCNF()
    setChaveNFe(gerarChave(empAtual.empcgc, empAtual.empest, new Date().toISOString().slice(0,10), String(nr.data.serie), String(nr.data.numero), cNF))
  }

  // ── modo 'origem' ───────────────────────────────────────────────────────
  const chaveDigits      = chaveInput.replace(/\D/g,'')
  const podeBuscarChave  = chaveDigits.length === 44
  const podeBuscarCampos = !!(empcod && cnpj && serie && numBusca)
  const podeBuscar       = podeBuscarChave || podeBuscarCampos

  async function buscar() {
    setErroBusca(''); setCab(null); setItens([]); setProxNumero(''); setChaveNFe('')
    setBuscando(true)
    try {
      const url = podeBuscarChave
        ? `/nfe/origem/buscar?chave=${chaveDigits}`
        : `/nfe/origem/buscar?empcod=${empcod}&cnpj=${cnpj.replace(/\D/g,'')}&serie=${serie}&numero=${numBusca}`
      const r = await api.get(url)
      const { cabecalho, itens: its } = r.data
      setCab(cabecalho)
      setItens(its.map((it: any) => ({
        ...it,
        preco_unitario_orig: Number(it.preco_unitario),
        icms_base_orig: Number(it.icms_base), icms_valor_orig: Number(it.icms_valor),
        pis_base_orig:  Number(it.pis_base),  pis_valor_orig:  Number(it.pis_valor),
        cofins_base_orig: Number(it.cofins_base), cofins_valor_orig: Number(it.cofins_valor),
        selecionado: true, qtd_credito: String(it.quantidade), expandido: true,
        edit_icms_cst: it.icms_cst || '41',
        edit_icms_base: Number(it.icms_base), edit_icms_aliq: Number(it.icms_aliq), edit_icms_valor: Number(it.icms_valor),
        edit_st_base: 0, edit_st_aliq: 0, edit_st_valor: 0,
        edit_ipi_cst: '53', edit_ipi_base: 0, edit_ipi_aliq: 0, edit_ipi_valor: 0,
        edit_pis_cst: '07',
        edit_pis_base: Number(it.pis_base), edit_pis_aliq: Number(it.pis_aliq), edit_pis_valor: Number(it.pis_valor),
        edit_cofins_cst: '07',
        edit_cofins_base: Number(it.cofins_base), edit_cofins_aliq: Number(it.cofins_aliq), edit_cofins_valor: Number(it.cofins_valor),
        ibsCbs: it.ibs_cbs_cst ? {
          cst: it.ibs_cbs_cst, classTrib: it.ibs_cbs_classtrib, classTribDes: it.ibs_cbs_classtrib_des ?? '',
          cbsBc: Number(it.cbs_base), cbsBcEf: Number(it.cbs_bc_ef), cbsAliRef: Number(it.cbs_ali_ref), cbsRedAli: Number(it.cbs_red_ali), cbsAliEf: Number(it.cbs_ali_ef), cbsValor: Number(it.cbs_valor),
          ibsUfEst: it.ibs_uf_est ?? '', ibsUfBcEf: Number(it.ibs_uf_bc_ef), ibsUfAliRef: Number(it.ibs_uf_ali_ref), ibsUfRedAli: Number(it.ibs_uf_red_ali), ibsUfAliEf: Number(it.ibs_uf_ali_ef), ibsUfValor: Number(it.ibs_uf_valor),
          ibsMunBcEf: Number(it.ibs_mun_bc_ef), ibsMunAliRef: Number(it.ibs_mun_ali_ref), ibsMunRedAli: Number(it.ibs_mun_red_ali), ibsMunAliEf: Number(it.ibs_mun_ali_ef), ibsMunValor: Number(it.ibs_mun_valor),
          isSbas: Number(it.is_base), isSali: Number(it.is_aliq),
        } : {...ibsCbsVazio},
      })))
      const empAtual = empresa ?? empresas.find(e => e.empcod === cabecalho.empcod)
      if (empAtual) await buscarProximoNumero(empAtual)
    } catch (e: any) {
      setErroBusca(e.response?.data?.error ?? 'Nota não encontrada')
    } finally { setBuscando(false) }
  }

  // Reescala impostos clássicos com base na razão entre o valor a creditar (qtd × preço,
  // ambos editáveis conforme o tipo) e o valor total original do item (qtd × preço orig.)
  function recomputeImpostosOrigem(it: ItemCredito, qtdNovo: number, precoNovo: number) {
    const totalOrig = it.quantidade * it.preco_unitario_orig
    const ratio = totalOrig > 0 ? (qtdNovo * precoNovo) / totalOrig : 0
    return {
      edit_icms_base: r2(it.icms_base_orig * ratio),
      edit_icms_valor: r2(it.icms_valor_orig * ratio),
      edit_pis_base: r2(it.pis_base_orig * ratio),
      edit_pis_valor: r2(it.pis_valor_orig * ratio),
      edit_cofins_base: r2(it.cofins_base_orig * ratio),
      edit_cofins_valor: r2(it.cofins_valor_orig * ratio),
    }
  }
  function atualizarQtd(idx: number, qtd: string) {
    setItens(prev => prev.map((it, i) => i === idx
      ? { ...it, qtd_credito: qtd, ...recomputeImpostosOrigem(it, Number(qtd)||0, Number(it.preco_unitario)) }
      : it))
  }
  function atualizarPreco(idx: number, preco: string) {
    setItens(prev => prev.map((it, i) => {
      if (i !== idx) return it
      const precoNum = Number(preco) || 0
      return { ...it, preco_unitario: precoNum, ...recomputeImpostosOrigem(it, Number(it.qtd_credito)||0, precoNum) }
    }))
  }

  function editItem(idx: number, field: string, value: any) {
    setItens(prev => prev.map((it, i) => i === idx ? {...it, [field]: value} : it))
  }

  const itensSel = itens.filter(it => it.selecionado && (Number(it.qtd_credito)||0) > 0)
  const temQtdInvalida = itens.some(it => it.selecionado && (Number(it.qtd_credito)||0) > it.quantidade)
  const totalOrigem = itensSel.reduce((s,it) => s + r2((Number(it.qtd_credito)||0)*Number(it.preco_unitario)), 0)

  const nat = naturezas.find(n => String(n.tno_cod) === naturezaCod)

  function calcImpostosNat(n: Natureza, vProd: number) {
    const bc  = (base: number, p: number) => r2(base * (Number(p) / 100))
    const det = (base: number, a: number) => r2(base * (Number(a) / 100))
    const icmsBase   = bc(vProd, n.tno_bic);  const icmsValor   = det(icmsBase,   n.tno_aic)
    const ipiBase    = bc(vProd, n.tno_bin);  const ipiValor    = det(ipiBase,    n.tno_ain)
    const pisBase    = bc(vProd, n.tno_bpi);  const pisValor    = det(pisBase,    n.tno_api)
    const cofinsBase = bc(vProd, n.tno_bcf);  const cofinsValor = det(cofinsBase, n.tno_acf)
    return {
      edit_icms_cst: n.tno_tic?.trim() || '41',
      edit_icms_base: icmsBase, edit_icms_aliq: Number(n.tno_aic), edit_icms_valor: icmsValor,
      edit_ipi_cst: n.tno_tin?.trim() || '53',
      edit_ipi_base: ipiBase,   edit_ipi_aliq:  Number(n.tno_ain), edit_ipi_valor:  ipiValor,
      edit_pis_cst: n.tno_tpi?.trim() || '07',
      edit_pis_base: pisBase,   edit_pis_aliq:  Number(n.tno_api), edit_pis_valor:  pisValor,
      edit_cofins_cst: n.tno_tcf?.trim() || '07',
      edit_cofins_base: cofinsBase, edit_cofins_aliq: Number(n.tno_acf), edit_cofins_valor: cofinsValor,
    }
  }

  // Recalcula impostos clássicos dos itens herdados quando a natureza muda (só tipos com tributosClassicos=true)
  useEffect(() => {
    if (!nat || !itens.length || !tipoCfg.tributosClassicos) return
    setItens(prev => prev.map(it => {
      const vProd = r2((Number(it.qtd_credito) || it.quantidade) * Number(it.preco_unitario))
      return { ...it, ...calcImpostosNat(nat, vProd) }
    }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [naturezaCod])

  // ── tipo 01 (rateioValor): rateia o valor de multa/juros entre os itens
  //    selecionados, proporcional ao valor de cada um na nota original. Método
  //    do maior resto (largest remainder) — fecha exato no centavo, sem sobra.
  function calcRateioCents(pesos: number[], totalCents: number): number[] {
    const somaPesos = pesos.reduce((s, w) => s + w, 0)
    if (somaPesos <= 0 || totalCents <= 0) return pesos.map(() => 0)
    const brutos = pesos.map(w => (totalCents * w) / somaPesos)
    const bases = brutos.map(b => Math.floor(b))
    const resto = totalCents - bases.reduce((s, b) => s + b, 0)
    const ordem = brutos.map((b, i) => ({ i, frac: b - bases[i] })).sort((a, b) => b.frac - a.frac)
    const resultado = [...bases]
    for (let k = 0; k < resto; k++) resultado[ordem[k].i] += 1
    return resultado
  }
  function recalcIbsCbsRateio(ibs: IbsCbs, novoTotal: number): IbsCbs {
    if (!ibs.cst) return ibs
    const bcEfC = r2(novoTotal * (1 - ibs.cbsRedAli / 100))
    const bcEfU = r2(novoTotal * (1 - ibs.ibsUfRedAli / 100))
    const bcEfM = r2(novoTotal * (1 - ibs.ibsMunRedAli / 100))
    return {
      ...ibs,
      cbsBc: novoTotal, cbsBcEf: bcEfC, cbsValor: r2(bcEfC * ibs.cbsAliEf / 100),
      ibsUfBcEf: bcEfU, ibsUfValor: r2(bcEfU * ibs.ibsUfAliEf / 100),
      ibsMunBcEf: bcEfM, ibsMunValor: r2(bcEfM * ibs.ibsMunAliEf / 100),
      isSbas: ibs.isSbas > 0 ? novoTotal : 0,
    }
  }
  function itensRateioComputados() {
    const selecionados = itens.filter(it => it.selecionado)
    const pesos = selecionados.map(it => r2(it.quantidade * Number(it.preco_unitario)))
    const totalCents = Math.round((Number(valorMultaJuros) || 0) * 100)
    const centavos = calcRateioCents(pesos, totalCents)
    return selecionados.map((it, idx) => ({ it, valorRateado: centavos[idx] / 100 }))
  }

  // ── modo 'manual' ───────────────────────────────────────────────────────
  function handleBuscaCliente(valor: string) {
    setClienteBusca(valor); setClienteSel(null); setClienteEndereco(null)
    if (debounceCliente.current) clearTimeout(debounceCliente.current)
    if (valor.length < 2) { setClientesEncontrados([]); return }
    debounceCliente.current = setTimeout(async () => {
      setBuscandoCliente(true)
      try { const r = await api.get('/erp/clientes', { params: { busca: valor } }); setClientesEncontrados(r.data) }
      finally { setBuscandoCliente(false) }
    }, 400)
  }

  async function selecionarClienteSucessora(c: Cliente) {
    setClienteSel(c); setClienteBusca(c.cli_nom); setClientesEncontrados([])
    try {
      const r = await api.get(`/erp/clientes/${c.cli_cod}/endereco`)
      setClienteEndereco(r.data)
    } catch {}
    const empAtual = empresa ?? empresas[0]
    if (empAtual) await buscarProximoNumero(empAtual)
  }

  function addItemManual() { setItensManual(prev => [...prev, { ...itemManualVazio }]) }
  function removeItemManual(i: number) { setItensManual(prev => prev.filter((_, idx) => idx !== i)) }
  function updateItemManual(i: number, campo: keyof ItemManual, valor: any) {
    setItensManual(prev => prev.map((it, idx) => idx === i ? { ...it, [campo]: valor } : it))
  }

  const totalManual = itensManual.reduce((s, it) => s + it.quantidade * it.valorUnitario, 0)
  const itensManualValidos = itensManual.filter(it => it.cProd && it.quantidade > 0)

  async function emitir() {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab?.empcod)
    if (!empAtual || !nat) return

    if (tipoCfg.modo === 'origem') {
      if (!cab) return
      if (cab.bloqueado) { setErro('Destinatário bloqueado — não é possível emitir documentos para ele.'); return }
      if (tipoCfg.rateioValor && !(Number(valorMultaJuros) > 0)) {
        setErro('Informe o valor total de multa/juros pago.'); return
      }
    } else {
      if (!clienteSel) return
      if (clienteSel.bloqueado) { setErro('Destinatário bloqueado — não é possível emitir documentos para ele.'); return }
    }
    setEnviando(true); setErro(null)

    const cNF  = randCNF()
    const chave = gerarChave(empAtual.empcgc, empAtual.empest, dataEmissao, proxSerie, proxNumero, cNF)
    const chaveUsada = editId ? chaveNFe : chave

    let destinatario: any
    let indIEDest: '1'|'2'|'9'
    let idDest: '1'|'2'|'3'
    let itensNFe: any[]
    let nfRef: string[] | undefined

    if (tipoCfg.modo === 'origem') {
      const contri = cab!.contribuinte?.trim().toUpperCase()
      indIEDest = cab!.ie?.trim() ? '1' : contri==='S' ? '2' : '9'
      idDest = cab!.uf?.trim().toUpperCase() === empAtual.empest?.trim().toUpperCase() ? '1' : '2'
      destinatario = {
        doc: cab!.cnpj_fornecedor, fj: (cab!.fj?.trim().toUpperCase()==='F'?'F':'J') as 'J'|'F',
        nome: cab!.nome_fornecedor, ie: cab!.ie||undefined, indIEDest,
        endereco: { logradouro: cab!.logradouro, numero: cab!.numero_end, bairro: cab!.bairro, cidade: cab!.cidade, uf: cab!.uf, cep: cab!.cep, telefone: cab!.telefone||undefined },
      }
      itensNFe = tipoCfg.rateioValor
        ? itensRateioComputados().map(({ it, valorRateado }) => {
            const ibsCbsNovo = recalcIbsCbsRateio(it.ibsCbs, valorRateado)
            return {
              cProd: String(it.its_cod), cEAN: 'SEM GTIN',
              xProd: it.descricao, NCM: it.ncm, CEST: it.cest||undefined,
              CFOP: String(nat.tno_cdf), uCom: it.unidade,
              qCom: it.quantidade, vUnCom: valorRateado / it.quantidade, vProd: valorRateado, indTot: '1' as const,
              impostos: {
                icmsCst: '41', icmsOrig: '0', icmsBase: 0, icmsAliq: 0, icmsDest: 0,
                ipiCst: '53', pisCst: '07', pisBase: 0, pisAliq: 0, pisDest: 0,
                cofinsCst: '07', cofinsBase: 0, cofinsAliq: 0, cofinsDest: 0,
                ibsCbsCst:       ibsCbsNovo.cst||undefined,
                ibsCbsClassTrib: ibsCbsNovo.classTrib||undefined,
                cbsBcEf:   ibsCbsNovo.cbsBcEf||undefined,  cbsAliEf:   ibsCbsNovo.cbsAliEf||undefined,  cbsValor:   ibsCbsNovo.cbsValor||undefined,
                ibsUfBcEf: ibsCbsNovo.ibsUfBcEf||undefined, ibsUfAliEf: ibsCbsNovo.ibsUfAliEf||undefined, ibsUfValor: ibsCbsNovo.ibsUfValor||undefined,
                ibsMunBcEf:ibsCbsNovo.ibsMunBcEf||undefined,ibsMunAliEf:ibsCbsNovo.ibsMunAliEf||undefined,ibsMunValor:ibsCbsNovo.ibsMunValor||undefined,
              },
            }
          })
        : itensSel.map(it => {
            const qtd   = Number(it.qtd_credito)||0
            const vProd = r2(qtd * Number(it.preco_unitario))
            return {
              cProd: String(it.its_cod), cEAN: 'SEM GTIN',
              xProd: it.descricao, NCM: it.ncm, CEST: it.cest||undefined,
              CFOP: String(nat.tno_cdf), uCom: it.unidade,
              qCom: qtd, vUnCom: Number(it.preco_unitario), vProd, indTot: '1' as const,
              impostos: {
                icmsCst: it.edit_icms_cst||'41', icmsOrig: '0',
                icmsBase: it.edit_icms_base, icmsAliq: it.edit_icms_aliq, icmsDest: it.edit_icms_valor,
                stBase: it.edit_st_base||undefined, stAliq: it.edit_st_aliq||undefined, stDest: it.edit_st_valor||undefined,
                ipiCst: it.edit_ipi_cst||'53', ipiBase: it.edit_ipi_base||undefined, ipiAliq: it.edit_ipi_aliq||undefined, ipiDest: it.edit_ipi_valor||undefined,
                pisCst: it.edit_pis_cst||'07', pisBase: it.edit_pis_base, pisAliq: it.edit_pis_aliq, pisDest: it.edit_pis_valor,
                cofinsCst: it.edit_cofins_cst||'07', cofinsBase: it.edit_cofins_base, cofinsAliq: it.edit_cofins_aliq, cofinsDest: it.edit_cofins_valor,
                ibsCbsCst:       it.ibsCbs.cst||undefined,
                ibsCbsClassTrib: it.ibsCbs.classTrib||undefined,
                cbsBcEf:   it.ibsCbs.cbsBcEf||undefined,  cbsAliEf:   it.ibsCbs.cbsAliEf||undefined,  cbsValor:   it.ibsCbs.cbsValor||undefined,
                ibsUfBcEf: it.ibsCbs.ibsUfBcEf||undefined, ibsUfAliEf: it.ibsCbs.ibsUfAliEf||undefined, ibsUfValor: it.ibsCbs.ibsUfValor||undefined,
                ibsMunBcEf:it.ibsCbs.ibsMunBcEf||undefined,ibsMunAliEf:it.ibsCbs.ibsMunAliEf||undefined,ibsMunValor:it.ibsCbs.ibsMunValor||undefined,
              },
            }
          })
      nfRef = [cab!.chave]
    } else {
      const c = clienteSel!
      indIEDest = c.cli_ins?.trim() ? '1' : c.cli_contri === 'S' ? '2' : '9'
      idDest = (clienteEndereco?.uf ?? c.cli_est)?.trim().toUpperCase() === empAtual.empest?.trim().toUpperCase() ? '1' : '2'
      destinatario = {
        doc: c.cli_mat.replace(/\D/g, ''), fj: c.cli_fj as 'J'|'F',
        nome: c.cli_nom?.trim() || c.cli_fan, ie: c.cli_ins?.trim() || undefined, indIEDest,
        endereco: clienteEndereco ? {
          logradouro: clienteEndereco.logradouro, numero: clienteEndereco.numero, bairro: clienteEndereco.bairro,
          cidade: clienteEndereco.cidade, uf: clienteEndereco.uf, cep: clienteEndereco.cep,
          telefone: clienteEndereco.telefone, email: clienteEndereco.email,
        } : undefined,
      }
      itensNFe = itensManualValidos.map(it => ({
        cProd: it.cProd || '0', cEAN: 'SEM GTIN',
        xProd: it.descricao, NCM: it.NCM.replace(/\D/g,'').padStart(8,'0'), CEST: it.CEST||undefined,
        CFOP: String(nat.tno_cdf), uCom: it.unidade,
        qCom: it.quantidade, vUnCom: it.valorUnitario, vProd: r2(it.quantidade*it.valorUnitario), indTot: '1' as const,
        impostos: {
          icmsCst: '41', icmsOrig: '0', icmsBase: 0, icmsAliq: 0, icmsDest: 0,
          ipiCst: '53', pisCst: '07', pisBase: 0, pisAliq: 0, pisDest: 0,
          cofinsCst: '07', cofinsBase: 0, cofinsAliq: 0, cofinsDest: 0,
          ibsCbsCst:       it.ibsCbs.cst||undefined,
          ibsCbsClassTrib: it.ibsCbs.classTrib||undefined,
          cbsBcEf:   it.ibsCbs.cbsBcEf||undefined,  cbsAliEf:   it.ibsCbs.cbsAliEf||undefined,  cbsValor:   it.ibsCbs.cbsValor||undefined,
          ibsUfBcEf: it.ibsCbs.ibsUfBcEf||undefined, ibsUfAliEf: it.ibsCbs.ibsUfAliEf||undefined, ibsUfValor: it.ibsCbs.ibsUfValor||undefined,
          ibsMunBcEf:it.ibsCbs.ibsMunBcEf||undefined,ibsMunAliEf:it.ibsCbs.ibsMunAliEf||undefined,ibsMunValor:it.ibsCbs.ibsMunValor||undefined,
        },
      }))
      nfRef = undefined
    }

    const formPayload = {
      _tipo: 'NOTA_CREDITO',
      empcod: empAtual.empcod, tipoCredito,
      cab, itens, clienteSel, clienteEndereco, itensManual, valorMultaJuros,
      naturezaCod, dataEmissao, obs: obs.trim()||'',
      proxSerie, proxNumero, chaveNFe: chaveUsada,
    }

    const payload = {
      _formPayload: formPayload,
      empcod: empAtual.empcod, chave: chaveUsada, serie: proxSerie, numero: Number(proxNumero), dataEmissao,
      natureza: { cfop: nat.tno_cdf, descricao: nat.tno_des || nat.tno_dsc || 'NOTA DE CREDITO' },
      destinatario,
      // NT 2025.002-RTC, regra B25-110: Nota de Crédito exige tpNF=0 (Entrada)
      tpNF: '0' as const, idDest, finNFe: '5' as const, tpNFCredito: tipoCredito,
      indFinal: (destinatario.indIEDest === '1' ? '0' : '1') as '0'|'1', indPres: '0', modFrete: '9' as const,
      itens: itensNFe,
      tPag: '90', vPag: 0, obs: obs.trim()||undefined,
      nfRef,
    }

    try {
      let id: number
      if (editId) {
        await api.put(`/nfe/${editId}`, payload)
        id = Number(editId)
      } else {
        const rs = await api.post('/nfe/salvar', { ...payload, _idSalvo: idSalvoRef.current ?? undefined })
        id = rs.data.id
        idSalvoRef.current = id
      }

      let autorizado = false
      try {
        const rt = await api.post(`/nfe/${id}/transmitir`)
        if (rt.data.autorizado) {
          autorizado = true
          setResultado(`✓ Autorizado — Protocolo ${rt.data.protocolo}`)
        } else if (rt.data.cStat === '204') {
          setResultado(`cStat 204: Nota já transmitida anteriormente.\n\nUse "Consultar SEFAZ" na lista de transmissão para recuperar o protocolo.`)
        } else {
          setResultado(`REJEITADO\n\ncStat ${rt.data.cStat}: ${rt.data.xMotivo}`)
        }
      } catch (et: any) {
        const detalhe = et?.response?.data?.error || et?.message || 'Erro'
        try {
          const rs2 = await api.get(`/nfe/${id}/status`)
          if (rs2.data.situacao==='AUTORIZADA') { autorizado=true; setResultado(`✓ Autorizado — Protocolo ${rs2.data.protocolo}`) }
          else setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`)
        } catch { setResultado(`ERRO AO TRANSMITIR\n\n${detalhe}`) }
      }

      setEtapa(2)
      if (autorizado) setTimeout(() => navigate('/nfe/lista'), 2500)
    } catch (e: any) {
      if (e?.response?.data?.codigo === 'NUMERO_EM_USO') setProxNumero(String(e.response.data.proximo))
      setErro(e?.response?.data?.error || 'Erro ao emitir')
    } finally { setEnviando(false) }
  }

  // ─── Etapa 2: resultado ───────────────────────────────────────────────────

  if (etapa === 2) {
    const autorizado = resultado?.startsWith('✓')
    return (
      <div style={{ maxWidth:600, margin:'40px auto', textAlign:'center' }}>
        <div style={{ background: autorizado?'#f0fdf4':'#fef2f2', border:`1px solid ${autorizado?'#86efac':'#fca5a5'}`, borderRadius:8, padding:'32px 24px' }}>
          <div style={{ fontSize:'2rem', marginBottom:8 }}>{autorizado ? '✓' : '✗'}</div>
          <div style={{ fontWeight:700, fontSize:'1rem', color: autorizado?'#15803d':'#dc2626', marginBottom:12, whiteSpace:'pre-wrap' }}>
            {resultado}
          </div>
          <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
            <button className="btn btn-secondary" onClick={() => navigate('/nfe/lista')}>Ver Lista NF-e</button>
            {!autorizado && (
              <button className="btn btn-primary" onClick={() => { setEtapa(0); setCab(null); setItens([]); setClienteSel(null); setResultado(null) }}>
                Nova Nota de Crédito
              </button>
            )}
          </div>
        </div>
      </div>
    )
  }

  // ─── Etapa 1: Revisão ─────────────────────────────────────────────────────

  if (etapa === 1 && (tipoCfg.modo === 'origem' ? cab : clienteSel)) {
    const empAtual = empresa ?? empresas.find(e => e.empcod === cab?.empcod)
    const destNome = tipoCfg.modo === 'origem' ? cab!.nome_fornecedor : (clienteSel!.cli_nom?.trim() || clienteSel!.cli_fan)
    const destDoc  = tipoCfg.modo === 'origem' ? cab!.cnpj_fornecedor : clienteSel!.cli_mat
    const bloqueado = tipoCfg.modo === 'origem' ? cab?.bloqueado : clienteSel?.bloqueado
    const usaItensOrigem = tipoCfg.modo === 'origem' && !tipoCfg.rateioValor
    const linhasResumo = usaItensOrigem
      ? itensSel.map(it => ({ descricao: it.descricao, unidade: it.unidade, qtd: Number(it.qtd_credito)||0, vUnit: Number(it.preco_unitario), cst: it.edit_icms_cst, aliq: it.edit_icms_aliq, ibsCst: it.ibsCbs.cst }))
      : tipoCfg.rateioValor
        ? itensRateioComputados().map(({ it, valorRateado }) => ({ descricao: it.descricao, unidade: it.unidade, qtd: it.quantidade, vUnit: r2(valorRateado / it.quantidade), cst: '', aliq: 0, ibsCst: it.ibsCbs.cst }))
        : itensManualValidos.map(it => ({ descricao: it.descricao, unidade: it.unidade, qtd: it.quantidade, vUnit: it.valorUnitario, cst: '', aliq: 0, ibsCst: it.ibsCbs.cst }))
    const totalRevisao = usaItensOrigem ? totalOrigem : (tipoCfg.rateioValor ? Number(valorMultaJuros)||0 : totalManual)

    return (
      <div style={{ maxWidth:900, margin:'0 auto' }}>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:10 }}>Revisão da Nota de Crédito — {tipoCfg.label}</div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
            <div><span style={{ color:'#9ca3af' }}>Empresa:</span> {empAtual?.empnom}</div>
            <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {proxSerie}/{proxNumero}</div>
            <div><span style={{ color:'#9ca3af' }}>CFOP:</span> {nat?.tno_cdf} — {nat?.tno_dsc}</div>
            <div><span style={{ color:'#9ca3af' }}>Destinatário:</span> <strong>{destNome}</strong></div>
            <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(destDoc)}</div>
            <div><span style={{ color:'#9ca3af' }}>Data:</span> {dataEmissao}</div>
            {tipoCfg.modo === 'origem' && (
              <div style={{ gridColumn:'1/-1' }}>
                <span style={{ color:'#9ca3af' }}>NF origem:</span>{' '}
                <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>{cab!.chave}</span>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:600, fontSize:'0.85rem', marginBottom:8 }}>Itens ({linhasResumo.length})</div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.8rem' }}>
            <thead>
              <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                <th style={{ padding:'5px 8px', textAlign:'left' }}>Descrição</th>
                <th style={{ padding:'5px 8px', textAlign:'center' }}>Un</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Qtd</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Vl Unit</th>
                <th style={{ padding:'5px 8px', textAlign:'right' }}>Total</th>
                <th style={{ padding:'5px 8px', textAlign:'center' }}>IBS/CBS</th>
              </tr>
            </thead>
            <tbody>
              {linhasResumo.map((it, idx) => (
                <tr key={idx} style={{ borderBottom:'1px solid #f3f4f6' }}>
                  <td style={{ padding:'5px 8px' }}>{it.descricao}</td>
                  <td style={{ padding:'5px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right' }}>{it.qtd.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right', color:'#6b7280' }}>{it.vUnit.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                  <td style={{ padding:'5px 8px', textAlign:'right', fontWeight:600 }}>{brl(r2(it.qtd*it.vUnit))}</td>
                  <td style={{ padding:'5px 8px', textAlign:'center', color: it.ibsCst?'#1d4ed8':'#9ca3af', fontSize:'0.75rem' }}>{it.ibsCst || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <div>
              <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>{linhasResumo.length} {linhasResumo.length===1?'item':'itens'}</div>
              <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(totalRevisao)}</div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              {erro && <span style={{ color:'#dc2626', fontSize:'0.82rem', maxWidth:300 }}>{erro}</span>}
              <button className="btn btn-secondary" onClick={() => setEtapa(0)}>← Voltar</button>
              <button className="btn btn-primary" onClick={emitir} disabled={enviando || !!bloqueado} style={{ minWidth:160 }}>
                {enviando ? 'Transmitindo...' : editId ? 'Salvar e Transmitir →' : 'Emitir NF-e →'}
              </button>
            </div>
          </div>
        </div>

      </div>
    )
  }

  // ─── Etapa 0: Tipo + Busca/Destinatário + Configuração ───────────────────

  return (
    <div style={{ maxWidth:900, margin:'0 auto' }}>
      <AvisoUltimaNota empcod={empresa?.empcod ?? cab?.empcod ?? empresas[0]?.empcod} modelo="55" serie={proxSerie} proximo={proxNumero} onNumeroAjustado={n => setProxNumero(String(n))} />

      {/* Empresa + Tipo de Crédito */}
      <div className="card" style={{ marginBottom:16 }}>
        <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
          <label style={{ fontWeight:600, fontSize:'0.85rem', whiteSpace:'nowrap' }}>Estabelecimento</label>
          <select value={empresa?.empcod??''} onChange={e => setEmpresa(empresas.find(x=>x.empcod===Number(e.target.value))??null)}
            style={{ flex:1, padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem' }}>
            {empresas.map(em => <option key={em.empcod} value={em.empcod}>{em.empnom}</option>)}
          </select>
        </div>
        <div>
          <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Tipo de Crédito</label>
          <select value={tipoCredito} onChange={e => trocarTipo(e.target.value)}
            style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem' }}>
            {TIPOS_CREDITO.map(t => (
              <option key={t.codigo} value={t.codigo} disabled={!t.habilitado}>
                {t.label}{!t.habilitado ? ` (em breve — ${t.motivo})` : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {tipoCfg.modo === 'origem' ? (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Buscar NF de origem (obrigatória para este tipo)</div>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Chave de acesso (44 dígitos)</label>
            <input value={chaveInput} onChange={e => setChaveInput(e.target.value.replace(/\D/g,'').slice(0,44))}
              placeholder="35250310..." maxLength={44}
              style={{ width:'100%', padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontFamily:'monospace', fontSize:'0.85rem', boxSizing:'border-box' }} />
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, margin:'10px 0', color:'#9ca3af', fontSize:'0.8rem' }}>
            <div style={{ flex:1, height:1, background:'#e5e7eb' }} />ou buscar por campos<div style={{ flex:1, height:1, background:'#e5e7eb' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 2fr 80px 120px', gap:8, marginBottom:12 }}>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Empcod</label>
              <input value={empcod} onChange={e=>setEmpcod(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>CNPJ/CPF Destinatário</label>
              <input value={cnpj} onChange={e=>setCnpj(e.target.value)} placeholder="00.000.000/0000-00" style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Série</label>
              <input value={serie} onChange={e=>setSerie(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Número</label>
              <input type="number" value={numBusca} onChange={e=>setNumBusca(e.target.value)} style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
            </div>
          </div>
          <button className="btn btn-primary" onClick={buscar} disabled={buscando||!podeBuscar} style={{ width:'100%' }}>
            {buscando?'Buscando...':'Buscar'}
          </button>
          {erroBusca && <div style={{ marginTop:10, padding:'8px 12px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:6, color:'#dc2626', fontSize:'0.85rem' }}>{erroBusca}</div>}
        </div>
      ) : (
        <div className="card" style={{ marginBottom:16 }}>
          <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>{tipoCfg.destinatarioLabel ?? 'Destinatário'}</div>
          {!clienteSel && (
            <div>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Nome, fantasia ou CNPJ/CPF</label>
              <div style={{ display:'flex', gap:8 }}>
                <input value={clienteBusca} onChange={e => handleBuscaCliente(e.target.value)} placeholder="Digite para buscar..." autoFocus
                  style={{ flex:1, padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
                {buscandoCliente && <span style={{ alignSelf:'center', fontSize:'0.8rem', color:'#9ca3af' }}>Buscando...</span>}
              </div>
              {clientesEncontrados.length > 0 && (
                <div style={{ marginTop:8, border:'1px solid #e5e7eb', borderRadius:6, maxHeight:220, overflowY:'auto' }}>
                  {clientesEncontrados.map(c => (
                    <button key={c.cli_cod} onClick={() => selecionarClienteSucessora(c)}
                      style={{ display:'block', width:'100%', textAlign:'left', padding:'8px 10px', border:'none', borderBottom:'1px solid #f3f4f6', background:'#fff', cursor:'pointer' }}>
                      <div style={{ fontWeight:600, fontSize:'0.85rem' }}>{c.cli_nom}</div>
                      <div style={{ fontSize:'0.75rem', color:'#6b7280' }}>{c.cli_fj==='J'?'CNPJ':'CPF'}: {formatDoc(c.cli_mat)}{c.cli_est?.trim() ? ` · UF ${c.cli_est}`:''}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {clienteSel && (
            <div>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{clienteSel.cli_nom}</div>
                  <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>{clienteSel.cli_fj==='J'?'CNPJ':'CPF'}: {formatDoc(clienteSel.cli_mat)} · IE: {clienteSel.cli_ins?.trim() || 'Isento'}</div>
                  {clienteEndereco && (
                    <div style={{ fontSize:'0.8rem', color:'#6b7280' }}>
                      {clienteEndereco.logradouro}{clienteEndereco.numero?`, ${clienteEndereco.numero}`:''}{clienteEndereco.bairro?` - ${clienteEndereco.bairro}`:''}{clienteEndereco.cidade?`, ${clienteEndereco.cidade}/${clienteEndereco.uf}`:''}{clienteEndereco.cep?` - CEP ${clienteEndereco.cep}`:''}
                    </div>
                  )}
                </div>
                {proxNumero && (
                  <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe', borderRadius:6, padding:'4px 12px', fontSize:'0.82rem', color:'#1d4ed8' }}>
                    Próximo nº: <strong>Série {proxSerie} · Nº {proxNumero}</strong>
                  </div>
                )}
              </div>
              {clienteSel.bloqueado && (
                <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.5rem' }}>
                  ⚠ DESTINATÁRIO BLOQUEADO — não é possível emitir documentos para ele.
                </div>
              )}
              <button onClick={() => { setClienteSel(null); setClienteBusca(''); setClienteEndereco(null) }}
                style={{ marginTop:8, fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>
                Trocar destinatário
              </button>
            </div>
          )}
        </div>
      )}

      {(tipoCfg.modo === 'origem' ? cab : clienteSel) && (
        <>
          {tipoCfg.modo === 'origem' && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', color:'#374151', marginBottom:10 }}>Nota de Origem</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'6px 24px', fontSize:'0.85rem' }}>
                <div><span style={{ color:'#9ca3af' }}>Destinatário:</span> <strong>{cab!.nome_fornecedor}</strong></div>
                <div><span style={{ color:'#9ca3af' }}>CNPJ:</span> {formatDoc(cab!.cnpj_fornecedor)}</div>
                <div><span style={{ color:'#9ca3af' }}>IE:</span> {cab!.ie?.trim() || 'Isento'}</div>
                <div><span style={{ color:'#9ca3af' }}>UF:</span> {cab!.uf}</div>
                <div><span style={{ color:'#9ca3af' }}>Série/Nº:</span> {cab!.serie}/{cab!.numero}</div>
                <div><span style={{ color:'#9ca3af' }}>Emissão:</span> {cab!.data_emissao?.slice(0,10)}</div>
                <div><span style={{ color:'#9ca3af' }}>Total:</span> {brl(Number(cab!.valor_total))}</div>
                <div style={{ gridColumn:'1/-1' }}>
                  <span style={{ color:'#9ca3af' }}>Endereço:</span>{' '}
                  {cab!.logradouro}{cab!.numero_end?`, ${cab!.numero_end}`:''}{cab!.bairro?` - ${cab!.bairro}`:''}{cab!.cidade?`, ${cab!.cidade}/${cab!.uf}`:''}{cab!.cep?` - CEP ${cab!.cep}`:''}
                </div>
                <div style={{ gridColumn:'1/-1' }}>
                  <span style={{ color:'#9ca3af' }}>Chave:</span>{' '}
                  <span style={{ fontFamily:'monospace', fontSize:'0.75rem' }}>{cab!.chave}</span>
                </div>
              </div>
              <div style={{ background: '#eef2ff', color: '#3730a3', fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.75rem' }}>
                Destinatário, CST e cClassTrib herdados da nota de origem — campos bloqueados.
              </div>
              {cab!.bloqueado && (
                <div style={{ background: '#fee2e2', color: '#991b1b', fontWeight: 700, fontSize: '0.85rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginTop: '0.5rem' }}>
                  ⚠ DESTINATÁRIO BLOQUEADO — não é possível emitir documentos para ele.
                </div>
              )}
            </div>
          )}

          {/* Configuração */}
          <div className="card" style={{ marginBottom:16 }}>
            <div style={{ fontWeight:700, marginBottom:12, fontSize:'0.95rem' }}>Dados da Nota de Crédito</div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Natureza da Operação / CFOP</label>
              {tipoCfg.cfopRestricao === 'devolucao' && (
                <div style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.8rem', padding: '0.5rem 0.75rem', borderRadius: 4, marginBottom: 8 }}>
                  ⚠ Só são aceitos CFOPs de devolução (regra I08-141): <strong>1202, 1553, 2202, 2553, 5202 ou 6202</strong>.
                </div>
              )}
              <NaturezaSelect naturezas={naturezasFiltradas} value={naturezaCod} onChange={setNaturezaCod} />
            </div>
            {tipoCfg.rateioValor && (
              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                  Valor total de Multa/Juros pago
                </label>
                <input type="number" min={0} step={0.01} value={valorMultaJuros} onChange={e => setValorMultaJuros(e.target.value)}
                  placeholder="0,00"
                  style={{ width:220, padding:'7px 10px', borderRadius:6, border:'1px solid #d1d5db', fontSize:'0.9rem', boxSizing:'border-box' }} />
                <div style={{ fontSize:'0.78rem', color:'#9ca3af', marginTop:4 }}>
                  Rateado automaticamente entre os itens selecionados abaixo, proporcional ao valor de cada um na nota original.
                </div>
              </div>
            )}
            <div style={{ display:'grid', gridTemplateColumns:'160px 1fr', gap:12 }}>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Data Emissão</label>
                <input type="date" value={dataEmissao} onChange={e=>setDataEmissao(e.target.value)}
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
              <div>
                <label style={{ fontSize:'0.8rem', color:'#6b7280', display:'block', marginBottom:4 }}>Observação</label>
                <input value={obs} onChange={e=>setObs(e.target.value)} maxLength={200} placeholder="Opcional"
                  style={{ width:'100%', padding:'7px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} />
              </div>
            </div>
          </div>

          {/* Itens — modo origem: herdados da nota original */}
          {tipoCfg.modo === 'origem' && !tipoCfg.rateioValor && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontWeight:700, fontSize:'0.95rem' }}>Itens</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:true,qtd_credito:String(it.quantidade)})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Todos</button>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:false})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Nenhum</button>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                  <thead>
                    <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                      <th style={{ padding:'6px 8px', width:32 }}></th>
                      <th style={{ padding:'6px 8px', textAlign:'left' }}>Descrição</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>Un</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd Original</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd a Creditar</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Vl Unit</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Total</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>Impostos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it, idx) => {
                      const qtdCredito = Number(it.qtd_credito)||0
                      const qtdOrig  = Number(it.quantidade)
                      const excede   = it.selecionado && qtdCredito > qtdOrig
                      const parcial  = it.selecionado && qtdCredito > 0 && qtdCredito < qtdOrig
                      const bordaCor = excede?'#f87171':parcial?'#fbbf24':'#d1d5db'
                      const bgInput  = excede?'#fff1f2':parcial?'#fffbeb':undefined
                      const totalIt  = r2(qtdCredito * Number(it.preco_unitario))
                      return (
                        <Fragment key={idx}>
                          <tr style={{ borderBottom: it.expandido?'none':(excede||parcial)?'none':'1px solid #f3f4f6', background:it.selecionado?'#fff':'#fafafa', opacity:it.selecionado?1:0.5 }}>
                            <td style={{ padding:'6px 8px', textAlign:'center' }}>
                              <input type="checkbox" checked={it.selecionado} onChange={() => editItem(idx,'selecionado',!it.selecionado)} />
                            </td>
                            <td style={{ padding:'6px 8px', fontWeight:500 }}>{it.descricao}</td>
                            <td style={{ padding:'6px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                            <td style={{ padding:'6px 8px', textAlign:'right', color:'#6b7280' }}>{qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                            <td style={{ padding:'6px 4px', textAlign:'right' }}>
                              <input type="number" value={it.qtd_credito} onChange={e=>atualizarQtd(idx,e.target.value)} disabled={!it.selecionado} min={0} step="0.001"
                                style={{ width:80, textAlign:'right', padding:'3px 6px', borderRadius:4, border:`1px solid ${bordaCor}`, background:bgInput, fontSize:'0.82rem' }} />
                            </td>
                            <td style={{ padding:'6px 4px', textAlign:'right' }}>
                              {tipoCfg.valorEditavel ? (
                                <input type="number" value={it.preco_unitario} onChange={e=>atualizarPreco(idx,e.target.value)} disabled={!it.selecionado} min={0} step="0.01"
                                  style={{ width:90, textAlign:'right', padding:'3px 6px', borderRadius:4, border:'1px solid #d1d5db', fontSize:'0.82rem' }} />
                              ) : Number(it.preco_unitario).toLocaleString('pt-BR',{minimumFractionDigits:2})}
                            </td>
                            <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:600 }}>{brl(totalIt)}</td>
                            <td style={{ padding:'6px 8px', textAlign:'center' }}>
                              <button onClick={() => editItem(idx,'expandido',!it.expandido)} disabled={!it.selecionado}
                                style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer', color:'#374151' }}>
                                {it.expandido ? 'Fechar ▲' : 'Ver ▼'}
                              </button>
                            </td>
                          </tr>
                          {(excede || parcial) && (
                            <tr key={`${idx}-aviso`} style={{ borderBottom: it.expandido?'none':'1px solid #f3f4f6' }}>
                              <td colSpan={8} style={{ padding:'2px 8px 6px 44px', fontSize:'0.75rem', color:excede?'#dc2626':'#92400e' }}>
                                {excede ? `Quantidade maior que a original (${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})})` : `Crédito parcial — original: ${qtdOrig.toLocaleString('pt-BR',{minimumFractionDigits:2})}`}
                              </td>
                            </tr>
                          )}
                          {it.expandido && (
                            <tr style={{ borderBottom:'1px solid #e5e7eb', background:'#f8fafc' }}>
                              <td colSpan={8} style={{ padding:'10px 16px 14px 36px' }}>
                                <div className="fiscal-tabs">
                                  <button type="button"
                                    className={`fiscal-tab${abaItem(idx)==='impostos'?' fiscal-tab--ativa':''}`}
                                    onClick={() => setAbaFiscal(p=>({...p,[idx]:'impostos'}))}>Impostos</button>
                                  <button type="button"
                                    className={`fiscal-tab${abaItem(idx)==='ibscbs'?' fiscal-tab--ativa':''}`}
                                    onClick={() => setAbaFiscal(p=>({...p,[idx]:'ibscbs'}))}>IBS/CBS</button>
                                </div>

                                {abaItem(idx) === 'impostos' && (
                                  <table className="imp-table">
                                    <thead>
                                      <tr>
                                        <th style={{ width:70 }}>Tributo</th>
                                        <th style={{ width:125 }}>Base Cálculo</th>
                                        <th style={{ width:75 }}>Alíquota</th>
                                        <th style={{ width:125 }}>Destaque</th>
                                        <th style={{ width:60 }}>CST</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {[
                                        { label:'ICMS',    bc:'edit_icms_base',   aliq:'edit_icms_aliq',   dest:'edit_icms_valor',   cst:'edit_icms_cst' },
                                        { label:'ICMS ST', bc:'edit_st_base',     aliq:'edit_st_aliq',     dest:'edit_st_valor',     cst:null },
                                        { label:'IPI',     bc:'edit_ipi_base',    aliq:'edit_ipi_aliq',    dest:'edit_ipi_valor',    cst:'edit_ipi_cst' },
                                        { label:'PIS',     bc:'edit_pis_base',    aliq:'edit_pis_aliq',    dest:'edit_pis_valor',    cst:'edit_pis_cst' },
                                        { label:'COFINS',  bc:'edit_cofins_base', aliq:'edit_cofins_aliq', dest:'edit_cofins_valor', cst:'edit_cofins_cst' },
                                      ].map(row => (
                                        <tr key={row.label}>
                                          <td className="imp-label">{row.label}</td>
                                          <td><input type="number" className="imp-input" min={0} step={0.01}
                                            value={(it as any)[row.bc]}
                                            onChange={e => editItem(idx, row.bc, Number(e.target.value))} /></td>
                                          <td><input type="number" className="imp-input imp-aliq" min={0} step={0.001}
                                            value={(it as any)[row.aliq]}
                                            onChange={e => editItem(idx, row.aliq, Number(e.target.value))} /></td>
                                          <td><input type="number" className="imp-input" min={0} step={0.01}
                                            value={(it as any)[row.dest]}
                                            onChange={e => editItem(idx, row.dest, Number(e.target.value))} /></td>
                                          <td>{row.cst
                                            ? <input type="text" className="imp-input imp-cst"
                                                value={(it as any)[row.cst]}
                                                onChange={e => editItem(idx, row.cst!, e.target.value)} />
                                            : <span className="imp-vazio">—</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}

                                {abaItem(idx) === 'ibscbs' && (
                                  <div>
                                    <table className="imp-table" style={{ marginTop:'0.35rem' }}>
                                      <thead>
                                        <tr>
                                          <th style={{ width:65 }}>Tributo</th>
                                          <th style={{ width:55 }}>CST</th>
                                          <th style={{ width:110 }}>Base Cálculo</th>
                                          <th style={{ width:115 }}>BC Efetiva</th>
                                          <th style={{ width:75 }}>Ali. Ref %</th>
                                          <th style={{ width:75 }}>Red. Ali %</th>
                                          <th style={{ width:75 }}>Ali. Efet %</th>
                                          <th style={{ width:110 }}>Valor</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td className="imp-label">CBS</td>
                                          <td style={{ textAlign:'center', fontSize:'0.78rem' }}>{it.ibsCbs.cst}</td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBcEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsAliRef.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsRedAli.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsAliEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                        </tr>
                                        <tr>
                                          <td className="imp-label">IBS UF</td>
                                          <td style={{ textAlign:'center', fontSize:'0.78rem', color:'#6b7280' }}>{it.ibsCbs.ibsUfEst}</td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfBcEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfAliRef.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfRedAli.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfAliEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsUfValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                        </tr>
                                        <tr>
                                          <td className="imp-label">IBS MUN</td>
                                          <td></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.cbsBc.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunBcEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunAliRef.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunRedAli.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunAliEf.toFixed(2)} /></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.ibsMunValor.toFixed(2)} style={{ fontWeight:800, color:'#1a6ea8' }} /></td>
                                        </tr>
                                        <tr>
                                          <td className="imp-label">IS</td>
                                          <td></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.isSbas.toFixed(2)} /></td>
                                          <td colSpan={3}></td>
                                          <td><input className="imp-input" type="text" readOnly value={it.ibsCbs.isSali.toFixed(2)} /></td>
                                          <td></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    {it.ibsCbs.classTrib && (
                                      <div style={{ marginTop:'0.35rem', fontSize:'0.75rem', color:'#6b7280' }}>
                                        <strong>Class. Trib.:</strong> {it.ibsCbs.classTrib} — {it.ibsCbs.classTribDes}
                                      </div>
                                    )}
                                    {!it.ibsCbs.cst && (
                                      <div style={{ padding:'12px 0', fontSize:'0.82rem', color:'#9ca3af', textAlign:'center' }}>
                                        Nenhuma regra IBS/CBS herdada da origem
                                      </div>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Itens — tipo 01 (rateioValor): herdados da nota, creditados por valor rateado */}
          {tipoCfg.modo === 'origem' && tipoCfg.rateioValor && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
                <span style={{ fontWeight:700, fontSize:'0.95rem' }}>Itens (rateio de multa/juros)</span>
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:true})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Todos</button>
                  <button onClick={() => setItens(prev=>prev.map(it=>({...it,selecionado:false})))}
                    style={{ fontSize:'0.78rem', padding:'3px 10px', borderRadius:4, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>Nenhum</button>
                </div>
              </div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                  <thead>
                    <tr style={{ background:'#f9fafb', borderBottom:'2px solid #e5e7eb' }}>
                      <th style={{ padding:'6px 8px', width:32 }}></th>
                      <th style={{ padding:'6px 8px', textAlign:'left' }}>Descrição</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>Un</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Qtd</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Vl Unit Original</th>
                      <th style={{ padding:'6px 8px', textAlign:'right' }}>Valor Rateado</th>
                      <th style={{ padding:'6px 8px', textAlign:'center' }}>cClassTrib</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rateioMap = new Map(itensRateioComputados().map(r => [r.it, r.valorRateado]))
                      return itens.map((it, idx) => {
                        const valorRateado = rateioMap.get(it)
                        return (
                          <tr key={idx} style={{ borderBottom:'1px solid #f3f4f6', background:it.selecionado?'#fff':'#fafafa', opacity:it.selecionado?1:0.5 }}>
                            <td style={{ padding:'6px 8px', textAlign:'center' }}>
                              <input type="checkbox" checked={it.selecionado} onChange={() => editItem(idx,'selecionado',!it.selecionado)} />
                            </td>
                            <td style={{ padding:'6px 8px', fontWeight:500 }}>{it.descricao}</td>
                            <td style={{ padding:'6px 8px', textAlign:'center', color:'#6b7280' }}>{it.unidade}</td>
                            <td style={{ padding:'6px 8px', textAlign:'right', color:'#6b7280' }}>{Number(it.quantidade).toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                            <td style={{ padding:'6px 8px', textAlign:'right', color:'#6b7280' }}>{Number(it.preco_unitario).toLocaleString('pt-BR',{minimumFractionDigits:2})}</td>
                            <td style={{ padding:'6px 8px', textAlign:'right', fontWeight:600 }}>{valorRateado!==undefined ? brl(valorRateado) : '—'}</td>
                            <td style={{ padding:'6px 8px', textAlign:'center', color: it.ibsCbs.cst?'#1d4ed8':'#9ca3af', fontSize:'0.75rem' }}>
                              {it.ibsCbs.classTrib || '—'}
                            </td>
                          </tr>
                        )
                      })
                    })()}
                  </tbody>
                </table>
              </div>
              {!(Number(valorMultaJuros) > 0) && (
                <div style={{ marginTop:10, fontSize:'0.8rem', color:'#9ca3af' }}>Informe o valor total de multa/juros acima para ratear entre os itens.</div>
              )}
            </div>
          )}

          {/* Itens — modo manual: informados pelo usuário (ex.: tipo 05) */}
          {(tipoCfg.modo === 'manual') && (
            <div className="card" style={{ marginBottom:16 }}>
              <div style={{ fontWeight:700, fontSize:'0.95rem', marginBottom:10 }}>Itens</div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign:'left', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Produto</th>
                    <th style={{ width:'8%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Qtd</th>
                    <th style={{ width:'15%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Vl. Unit.</th>
                    <th style={{ width:'13%', padding:'4px 6px', fontSize:'0.78rem', color:'#6b7280' }}>Total</th>
                    <th style={{ width:'8%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {itensManual.map((it, i) => (
                    <Fragment key={i}>
                      <tr>
                        <td style={{ padding:'0.35rem 0.5rem' }}>
                          <ItemSelect somenteAtivos
                            codValue={it.cProd} descValue={it.descricao} unValue={it.unidade}
                            onChange={(cod, des, un, _precod, ncm, cest) => {
                              updateItemManual(i, 'cProd', cod)
                              setItensManual(prev => prev.map((x, idx) => idx===i ? {
                                ...x, cProd: cod, descricao: des, unidade: un,
                                NCM: ncm?.replace(/\D/g,'') || x.NCM, CEST: cest?.replace(/\D/g,'') || x.CEST,
                              } : x))
                            }}
                          />
                        </td>
                        <td><input type="number" min={0} value={it.quantidade} onChange={e => updateItemManual(i, 'quantidade', Number(e.target.value))}
                          style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} /></td>
                        <td><input type="number" min={0} step={0.01} value={it.valorUnitario} onChange={e => updateItemManual(i, 'valorUnitario', Number(e.target.value))}
                          style={{ width:'100%', padding:'6px 8px', borderRadius:6, border:'1px solid #d1d5db', boxSizing:'border-box' }} /></td>
                        <td style={{ fontWeight:600 }}>{brl(it.quantidade * it.valorUnitario)}</td>
                        <td>
                          {itensManual.length > 1 && (
                            <button onClick={() => removeItemManual(i)}
                              style={{ fontSize:'0.75rem', padding:'2px 8px', borderRadius:4, border:'1px solid #fca5a5', background:'#fef2f2', color:'#dc2626', cursor:'pointer' }}>✕</button>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={5} style={{ padding:'0 0.5rem 0.75rem' }}>
                          <label style={{ fontSize:'0.78rem', color:'#6b7280', display:'block', marginBottom:4 }}>
                            IBS/CBS (Regra){tipoCfg.regraEsperada ? ` — ${tipoCfg.regraEsperada}` : ''}
                          </label>
                          <IbsCbsSelect
                            codValue={it.ibsCbsRegra} desValue={it.ibsCbsDes}
                            onChange={async (cod, des) => {
                              const total = it.quantidade * it.valorUnitario
                              let ibsCbs = { ...ibsCbsVazio }
                              if (cod) {
                                try {
                                  const r = await api.get(`/erp/ibscbs/${cod}`)
                                  if (r.data) ibsCbs = calcIbsCbs(total, r.data)
                                } catch {}
                              }
                              setItensManual(prev => prev.map((x, idx) => idx===i ? { ...x, ibsCbsRegra: cod, ibsCbsDes: des, ibsCbs } : x))
                            }}
                          />
                        </td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
              <button onClick={addItemManual}
                style={{ marginTop:8, fontSize:'0.82rem', padding:'5px 12px', borderRadius:6, border:'1px solid #d1d5db', background:'#f9fafb', cursor:'pointer' }}>
                + Adicionar item
              </button>
            </div>
          )}

          {/* Rodapé */}
          <div className="card">
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:'0.8rem', color:'#9ca3af' }}>
                  {tipoCfg.modo === 'origem'
                    ? (tipoCfg.rateioValor
                        ? `${itens.filter(it=>it.selecionado).length} ${itens.filter(it=>it.selecionado).length===1?'item':'itens'} selecionado${itens.filter(it=>it.selecionado).length!==1?'s':''}`
                        : `${itensSel.length} ${itensSel.length===1?'item':'itens'} selecionado${itensSel.length!==1?'s':''}`)
                    : `${itensManualValidos.length} ${itensManualValidos.length===1?'item':'itens'}`}
                </div>
                <div style={{ fontWeight:700, fontSize:'1.1rem' }}>Total: {brl(tipoCfg.modo === 'origem' ? (tipoCfg.rateioValor ? Number(valorMultaJuros)||0 : totalOrigem) : totalManual)}</div>
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setEtapa(1)}
                disabled={
                  !naturezaCod ||
                  (tipoCfg.modo === 'origem'
                    ? (tipoCfg.rateioValor
                        ? (itens.filter(it=>it.selecionado).length===0 || !(Number(valorMultaJuros)>0) || cab?.bloqueado)
                        : (itensSel.length===0 || temQtdInvalida || cab?.bloqueado))
                    : (itensManualValidos.length===0 || clienteSel?.bloqueado))
                }
                style={{ minWidth:180 }}
              >
                Revisar →
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
